import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone } from 'lucide-react';
import { useRestaurantSettings } from '@/features/restaurant/useRestaurantSettings';

/**
 * Mobile Sticky-Call-Bar (11.CTA1 / 05.M1): Die primäre Conversion
 * (Anruf/Reservierung) liegt dauerhaft in der Daumenzone.
 *
 * - nur < lg, erscheint nach ~1,5 Bildschirmhöhen Scroll (KB-Trigger)
 * - Targets ≥ 48 px, safe-area-inset für iOS
 * - auf /reservieren ausgeblendet (dort läuft die Conversion bereits)
 * - reserviert Platz am Body-Ende, damit Footer/Impressum-Link nicht
 *   verdeckt werden (11.RECHT1)
 */
export function StickyCallBar() {
  const [visible, setVisible] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { data: settings } = useRestaurantSettings();

  const phone = settings?.phone ?? '02362 23279';
  const phoneHref = `tel:${phone.replace(/\s/g, '').replace(/^0/, '+49')}`;

  const hidden = location.pathname.startsWith('/reservieren');

  useEffect(() => {
    if (hidden) {
      setVisible(false);
      return;
    }
    const onScroll = () => {
      setVisible(window.scrollY > window.innerHeight * 1.5);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [hidden, location.pathname]);

  // Footer nicht verdecken: Body bekommt unten Platz in Bar-Höhe
  // (gleiche Technik wie früher beim Cookie-Banner, F-020).
  useEffect(() => {
    if (!visible) return;
    const apply = () => {
      const h = barRef.current?.offsetHeight ?? 0;
      document.body.style.paddingBottom = h ? `${h}px` : '';
      // CSS-Var für Elemente, die über der Bar schweben müssen
      // (Chatbot-Button/-Fenster, siehe index.css).
      document.documentElement.style.setProperty(
        '--sticky-call-bar-h',
        h ? `${h}px` : '0px',
      );
    };
    apply();
    window.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      document.body.style.paddingBottom = '';
      document.documentElement.style.setProperty('--sticky-call-bar-h', '0px');
    };
  }, [visible]);

  if (hidden || !visible) return null;

  return (
    <div
      ref={barRef}
      className="fixed inset-x-0 bottom-0 z-40 lg:hidden bg-paper/95 backdrop-blur-md border-t border-rule shadow-lg pb-[env(safe-area-inset-bottom)]"
    >
      <div className="grid grid-cols-2 gap-2 px-3 py-2">
        <a
          href={phoneHref}
          className="inline-flex min-h-12 items-center justify-center gap-2 border-[1.5px] border-gold-2 text-gold-2 font-sans text-xs tracking-[0.18em] font-semibold uppercase rounded-sm hover:bg-gold/10 transition-colors"
        >
          <Phone className="size-4" aria-hidden="true" />
          Anrufen
        </a>
        <Link
          to="/reservieren"
          className="inline-flex min-h-12 items-center justify-center gap-2 bg-gold-2 border-[1.5px] border-gold-2 text-paper font-sans text-xs tracking-[0.18em] font-semibold uppercase rounded-sm hover:bg-ink-2 hover:border-ink-2 transition-colors"
        >
          Reservieren
        </Link>
      </div>
    </div>
  );
}
